import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Plus, Wrench, Snowflake, AlertTriangle, CheckCircle2, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

const STATUS_LABEL = {
  operational: { label: "Operacional", tone: "bg-emerald-100 text-emerald-800" },
  maintenance: { label: "Em manutenção", tone: "bg-amber-100 text-amber-800" },
  broken: { label: "Quebrado", tone: "bg-rose-100 text-rose-800" },
  retired: { label: "Aposentado", tone: "bg-stone-200 text-stone-700" },
};

const M_STATUS = {
  scheduled: { label: "Agendada", tone: "bg-blue-100 text-blue-800" },
  in_progress: { label: "Em andamento", tone: "bg-amber-100 text-amber-800" },
  completed: { label: "Concluída", tone: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Cancelada", tone: "bg-stone-200 text-stone-700" },
};

export default function Equipment() {
  const [items, setItems] = useState([]);
  const [maintenances, setMaintenances] = useState([]);
  const [units, setUnits] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [openMaint, setOpenMaint] = useState(null);
  const [openType, setOpenType] = useState(false);
  const [typeForm, setTypeForm] = useState({ key: "", label: "" });
  const [types, setTypes] = useState({ builtin: [], custom: [] });
  const [form, setForm] = useState({
    name: "",
    type: "refrigerator",
    serial_number: "",
    brand: "",
    model: "",
    unit_id: "",
    notes: "",
  });
  const [maint, setMaint] = useState({
    type: "preventive",
    description: "",
    cost: 0,
    performed_by: "",
    scheduled_date: "",
  });

  // Build label map (built-in + custom)
  const typeLabel = (k) => {
    const all = [...(types.builtin || []), ...(types.custom || [])];
    const f = all.find((t) => t.key === k);
    return f?.label || k;
  };
  const allTypeOptions = [...(types.builtin || []), ...(types.custom || [])];

  const loadTypes = async () => {
    try {
      const { data } = await api.get("/equipment/types");
      setTypes(data);
    } catch (err) {
      console.error(err);
    }
  };

  const load = async () => {
    try {
      const [e, m] = await Promise.all([
        api.get("/equipment"),
        api.get("/maintenances"),
      ]);
      setItems(e.data);
      setMaintenances(m.data);
    } catch (err) {
      console.error(err);
    }
    api.get("/units").then(({ data }) => setUnits(data)).catch(() => {});
    loadTypes();
  };

  useEffect(() => {
    load();
  }, []);

  const createType = async () => {
    const key = typeForm.key.trim().toLowerCase().replace(/[\s-]/g, "_");
    const label = typeForm.label.trim();
    if (!label) {
      toast.error("Rótulo obrigatório");
      return;
    }
    if (!/^[a-z][a-z0-9_]{1,31}$/.test(key)) {
      toast.error("Chave inválida: minúsculas/números/underline, 2-32 chars");
      return;
    }
    try {
      const { data } = await api.post("/equipment/types", { key, label });
      toast.success(`Tipo "${data.label}" criado`);
      setTypeForm({ key: "", label: "" });
      setOpenType(false);
      await loadTypes();
      // Pre-select the new type in the form if we're inside Novo equipamento
      setForm((f) => ({ ...f, type: data.key }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao criar tipo");
    }
  };

  const removeType = async (id, key) => {
    if (!confirm(`Excluir o tipo "${typeLabel(key)}"?`)) return;
    try {
      await api.delete(`/equipment/types/${id}`);
      toast.success("Tipo removido");
      await loadTypes();
      // If current form is using the removed type, fallback to 'other'
      setForm((f) => (f.type === key ? { ...f, type: "other" } : f));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const create = async () => {
    if (!form.name.trim()) {
      toast.error("Nome obrigatório");
      return;
    }
    try {
      const payload = { ...form };
      if (!payload.unit_id) delete payload.unit_id;
      await api.post("/equipment", payload);
      toast.success("Equipamento cadastrado");
      setOpenNew(false);
      setForm({ name: "", type: "refrigerator", serial_number: "", brand: "", model: "", unit_id: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const remove = async (id) => {
    if (!confirm("Excluir este equipamento?")) return;
    try {
      await api.delete(`/equipment/${id}`);
      toast.success("Removido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const submitMaintenance = async () => {
    if (!maint.description.trim()) {
      toast.error("Descrição obrigatória");
      return;
    }
    try {
      const payload = {
        equipment_id: openMaint.id,
        type: maint.type,
        description: maint.description,
        cost: Number(maint.cost) || 0,
        performed_by: maint.performed_by,
        scheduled_date: maint.scheduled_date || null,
      };
      await api.post("/maintenances", payload);
      toast.success("Manutenção registrada");
      setOpenMaint(null);
      setMaint({ type: "preventive", description: "", cost: 0, performed_by: "", scheduled_date: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const completeMaint = async (id) => {
    try {
      await api.patch(`/maintenances/${id}`, { status: "completed" });
      toast.success("Manutenção concluída");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    }
  };

  const stats = useMemo(() => ({
    total: items.length,
    operational: items.filter((i) => i.status === "operational").length,
    maintenance: items.filter((i) => i.status === "maintenance").length,
    open_m: maintenances.filter((m) => m.status !== "completed" && m.status !== "cancelled").length,
  }), [items, maintenances]);

  return (
    <Layout
      title="Equipamentos"
      subtitle="Inventário operacional e manutenções preventivas/corretivas"
      actions={
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="border-stone-300"
            onClick={() => setOpenType(true)}
            data-testid="equipment-types-manage"
          >
            <Wrench className="h-4 w-4 mr-2" /> Tipos
          </Button>
          <Dialog open={openNew} onOpenChange={setOpenNew}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="equipment-new">
                <Plus className="h-4 w-4 mr-2" /> Novo equipamento
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">Novo equipamento</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Nome *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Ex: Câmara fria 1"
                  className="mt-1 bg-white"
                  data-testid="equipment-name"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="flex items-center justify-between">
                    <Label>Tipo</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50"
                      onClick={() => setOpenType(true)}
                      data-testid="equipment-type-new"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Novo tipo
                    </Button>
                  </div>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="equipment-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(types.builtin || []).map((t) => (
                        <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                      ))}
                      {(types.custom || []).length > 0 && (
                        <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-stone-400 border-t border-stone-200 mt-1 pt-2">
                          Personalizados
                        </div>
                      )}
                      {(types.custom || []).map((t) => (
                        <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Unidade</Label>
                  <Select value={form.unit_id || "none"} onValueChange={(v) => setForm({ ...form, unit_id: v === "none" ? "" : v })}>
                    <SelectTrigger className="mt-1 bg-white"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">—</SelectItem>
                      {units.map((u) => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Marca</Label>
                  <Input
                    value={form.brand}
                    onChange={(e) => setForm({ ...form, brand: e.target.value })}
                    className="mt-1 bg-white"
                  />
                </div>
                <div>
                  <Label>Modelo</Label>
                  <Input
                    value={form.model}
                    onChange={(e) => setForm({ ...form, model: e.target.value })}
                    className="mt-1 bg-white"
                  />
                </div>
              </div>
              <div>
                <Label>Número de série</Label>
                <Input
                  value={form.serial_number}
                  onChange={(e) => setForm({ ...form, serial_number: e.target.value })}
                  className="mt-1 bg-white"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={create}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="equipment-submit"
              >
                Cadastrar
              </Button>
            </DialogFooter>
          </DialogContent>
          </Dialog>
        </div>
      }
    >
      <div data-testid="equipment-page" className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Equipamentos", value: stats.total, icon: Snowflake, tone: "text-blue-700 bg-blue-100" },
            { label: "Operacionais", value: stats.operational, icon: CheckCircle2, tone: "text-emerald-700 bg-emerald-100" },
            { label: "Em manutenção", value: stats.maintenance, icon: Wrench, tone: "text-amber-700 bg-amber-100" },
            { label: "Manut. abertas", value: stats.open_m, icon: AlertTriangle, tone: "text-rose-700 bg-rose-100" },
          ].map((k) => (
            <Card key={k.label} className="p-5 rounded-xl border-stone-200 bg-white">
              <div className={`h-9 w-9 rounded-md flex items-center justify-center ${k.tone}`}>
                <k.icon className="h-4 w-4" />
              </div>
              <div className="mt-3 font-display text-3xl font-extrabold tracking-tight">{k.value}</div>
              <div className="text-xs text-stone-500 font-body uppercase tracking-wider mt-1">{k.label}</div>
            </Card>
          ))}
        </div>

        <Card className="rounded-xl border-stone-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-200">
            <h3 className="font-display font-bold tracking-tight">Inventário</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Nome</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Marca/Modelo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-stone-500 py-10">
                    Nenhum equipamento cadastrado
                  </TableCell>
                </TableRow>
              )}
              {items.map((eq) => (
                <TableRow key={eq.id} data-testid={`equipment-row-${eq.id}`}>
                  <TableCell>
                    <div className="font-display font-semibold">{eq.name}</div>
                    <div className="text-xs text-stone-500">{eq.serial_number || "—"}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="border-stone-200">
                      {typeLabel(eq.type)}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-body text-sm">
                    {[eq.brand, eq.model].filter(Boolean).join(" · ") || "—"}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${STATUS_LABEL[eq.status]?.tone} border-none`}>
                      {STATUS_LABEL[eq.status]?.label || eq.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="mr-2"
                      onClick={() => setOpenMaint(eq)}
                      data-testid={`equipment-maint-${eq.id}`}
                    >
                      <Wrench className="h-3.5 w-3.5 mr-1" /> Manutenção
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-rose-600"
                      onClick={() => remove(eq.id)}
                    >
                      Excluir
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Card className="rounded-xl border-stone-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-200">
            <h3 className="font-display font-bold tracking-tight">Manutenções</h3>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead>Equipamento</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Descrição</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Agendada</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {maintenances.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-stone-500 py-10">
                    Nenhuma manutenção registrada
                  </TableCell>
                </TableRow>
              )}
              {maintenances.map((m) => (
                <TableRow key={m.id} data-testid={`maint-row-${m.id}`}>
                  <TableCell className="font-display font-semibold">{m.equipment_name}</TableCell>
                  <TableCell className="font-body capitalize">{m.type}</TableCell>
                  <TableCell className="font-body text-sm line-clamp-2 max-w-xs">{m.description}</TableCell>
                  <TableCell>
                    <Badge className={`${M_STATUS[m.status]?.tone} border-none`}>
                      {M_STATUS[m.status]?.label || m.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-body text-xs text-stone-500">
                    {m.scheduled_date ? formatDateTime(m.scheduled_date) : "—"}
                  </TableCell>
                  <TableCell className="text-right">
                    {m.status !== "completed" && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => completeMaint(m.id)}
                        data-testid={`maint-complete-${m.id}`}
                      >
                        Concluir
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>

        <Dialog open={!!openMaint} onOpenChange={(o) => !o && setOpenMaint(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">
                Nova manutenção — {openMaint?.name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <Select value={maint.type} onValueChange={(v) => setMaint({ ...maint, type: v })}>
                    <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="preventive">Preventiva</SelectItem>
                      <SelectItem value="corrective">Corretiva</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Custo (R$)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={maint.cost}
                    onChange={(e) => setMaint({ ...maint, cost: e.target.value })}
                    className="mt-1 bg-white"
                  />
                </div>
              </div>
              <div>
                <Label>Descrição *</Label>
                <Textarea
                  rows={3}
                  value={maint.description}
                  onChange={(e) => setMaint({ ...maint, description: e.target.value })}
                  className="mt-1 bg-white"
                  data-testid="maint-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Realizada por</Label>
                  <Input
                    value={maint.performed_by}
                    onChange={(e) => setMaint({ ...maint, performed_by: e.target.value })}
                    className="mt-1 bg-white"
                  />
                </div>
                <div>
                  <Label>Data agendada</Label>
                  <Input
                    type="datetime-local"
                    value={maint.scheduled_date}
                    onChange={(e) => setMaint({ ...maint, scheduled_date: e.target.value })}
                    className="mt-1 bg-white"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={submitMaintenance}
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                data-testid="maint-submit"
              >
                Registrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Custom equipment type dialog */}
        <Dialog open={openType} onOpenChange={setOpenType}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">Tipos de equipamento</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="rounded-md bg-stone-50 border border-stone-200 p-3">
                <div className="text-xs font-body uppercase tracking-wider text-stone-500 mb-2">
                  Cadastrar novo tipo
                </div>
                <div className="grid grid-cols-5 gap-2">
                  <div className="col-span-3">
                    <Label className="text-xs">Rótulo *</Label>
                    <Input
                      value={typeForm.label}
                      onChange={(e) => {
                        const label = e.target.value;
                        setTypeForm((t) => ({
                          ...t,
                          label,
                          // auto-suggest key if user hasn't customized it
                          key: t.key
                            ? t.key
                            : label
                                .toLowerCase()
                                .normalize("NFD")
                                .replace(/[\u0300-\u036f]/g, "")
                                .replace(/[^a-z0-9]+/g, "_")
                                .replace(/^_+|_+$/g, "")
                                .slice(0, 32),
                        }));
                      }}
                      placeholder="Ex: Pass Through"
                      className="mt-1 bg-white"
                      data-testid="equipment-type-label"
                      autoFocus
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Chave</Label>
                    <Input
                      value={typeForm.key}
                      onChange={(e) => setTypeForm({ ...typeForm, key: e.target.value })}
                      placeholder="pass_through"
                      className="mt-1 bg-white font-mono text-sm"
                      data-testid="equipment-type-key"
                    />
                  </div>
                </div>
                <Button
                  onClick={createType}
                  className="mt-3 w-full bg-emerald-800 hover:bg-emerald-900 text-white"
                  data-testid="equipment-type-submit"
                >
                  <Plus className="h-4 w-4 mr-1" /> Adicionar tipo
                </Button>
              </div>

              {(types.custom || []).length > 0 && (
                <div>
                  <div className="text-xs font-body uppercase tracking-wider text-stone-500 mb-2">
                    Tipos personalizados ({types.custom.length})
                  </div>
                  <div className="space-y-1.5">
                    {types.custom.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-center justify-between rounded-md border border-stone-200 bg-white px-3 py-2"
                        data-testid={`equipment-type-row-${t.key}`}
                      >
                        <div>
                          <div className="font-display font-semibold text-sm">{t.label}</div>
                          <code className="text-[10px] text-stone-500">{t.key}</code>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-rose-600 hover:bg-rose-50"
                          onClick={() => removeType(t.id, t.key)}
                          data-testid={`equipment-type-delete-${t.key}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-[11px] text-stone-500 font-body leading-relaxed">
                Tipos padrão (Geladeira, Freezer, Forno, Coifa, Câmara fria, Outro)
                não podem ser removidos. Tipos personalizados em uso por equipamentos
                não podem ser excluídos até que os equipamentos sejam removidos ou
                migrados.
              </p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
